import type {
  AgentPage,
  AgentBlockListQuery,
  AgentInstanceListQuery,
} from '../../types/negotiationAgents';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { flowCustomGroupsService } from './services';
import type {
  FlowCustomGroup,
  FlowCustomGroupInstance,
  FlowCustomGroupInput,
  FlowCustomGroupInstanceInput,
} from '../../types/flows';

export const listFlowCustomGroups = createAsyncThunk<
  AgentPage<FlowCustomGroup>,
  AgentBlockListQuery | undefined
>('flowCustomGroups/list', async (query) => {
  return await flowCustomGroupsService.listGroups(query);
});

export const getFlowCustomGroup = createAsyncThunk<FlowCustomGroup, string>(
  'flowCustomGroups/getById',
  async (groupId) => {
    return await flowCustomGroupsService.getGroup(groupId);
  },
);

export const createFlowCustomGroup = createAsyncThunk<FlowCustomGroup, FlowCustomGroupInput>(
  'flowCustomGroups/create',
  async (payload) => {
    return await flowCustomGroupsService.createGroup(payload);
  },
);

export const updateFlowCustomGroup = createAsyncThunk<
  FlowCustomGroup,
  { groupId: string; payload: FlowCustomGroupInput }
>('flowCustomGroups/update', async ({ groupId, payload }) => {
  return await flowCustomGroupsService.updateGroup(groupId, payload);
});

export const deleteFlowCustomGroup = createAsyncThunk<string, string>(
  'flowCustomGroups/delete',
  async (groupId) => {
    await flowCustomGroupsService.deleteGroup(groupId);
    return groupId;
  },
);

export const listFlowCustomGroupInstances = createAsyncThunk<
  { flowId: string; page: AgentPage<FlowCustomGroupInstance> },
  string | AgentInstanceListQuery
>('flowCustomGroups/listInstances', async (input) => {
  const query = typeof input === 'string' ? { flowId: input } : input;
  const page = await flowCustomGroupsService.listInstances(query);
  return { flowId: query.flowId, page };
});

export const createFlowCustomGroupInstance = createAsyncThunk<
  FlowCustomGroupInstance,
  { flowId: string; payload: FlowCustomGroupInstanceInput }
>('flowCustomGroups/createInstance', async ({ flowId, payload }) => {
  return await flowCustomGroupsService.createInstance(flowId, payload);
});

export const updateFlowCustomGroupInstance = createAsyncThunk<
  FlowCustomGroupInstance,
  { flowId: string; instanceId: string; payload: FlowCustomGroupInstanceInput }
>('flowCustomGroups/updateInstance', async ({ flowId, instanceId, payload }) => {
  return await flowCustomGroupsService.updateInstance(flowId, instanceId, payload);
});

export const deleteFlowCustomGroupInstance = createAsyncThunk<
  { flowId: string; instanceId: string },
  { flowId: string; instanceId: string }
>('flowCustomGroups/deleteInstance', async ({ flowId, instanceId }) => {
  await flowCustomGroupsService.deleteInstance(flowId, instanceId);
  return { flowId, instanceId };
});
